/**
 * The app's own (generic) ceiling lights: a flush-mount dome, a pendant and a
 * chandelier — what a ceiling light draws as when it has no catalog model,
 * and what the light picker offers as its built-in tiles.
 *
 * Each is chosen by `proceduralStyle` on the object, opt-in: a model-less
 * ceiling light with no style keeps the fixed 4" puck the scene has always
 * drawn (`add_lighting`'s "modern" lights; the user, 2026-10-16), and a
 * recessed can keeps its own `'recessed'`.
 *
 * A hung light is described the way the catalog describes one — canopy /
 * stem / shade bands in inches, measured down from the canopy face, each with
 * its `maxRadius` (`materialRegions`) — so the elevation's `hungLightProportions`
 * reads a generic exactly as it reads a product, and the 3D drawing reads the
 * same bands: the two views cannot disagree about a generic's shape.
 *
 * Sizes are the dev catalog's medians, so a generic reads like the fixtures it
 * stands in for (2026-10-17): pendants n=24 — canopy 2.1" tall x 2.37" radius,
 * stem 0.83" radius, shade 13.2" tall x 6" radius; chandeliers n=8 — canopy
 * 3" x 4.8", stem 0.86", shade 11.6" x 17.5"; flush mounts n=9 — 4.7" tall. The dome's width is the 12"
 * standard the inspection uses for a light it could not size (the user,
 * 2026-10-16). The stem is the hang planner's own default for an unknown
 * ceiling (`PENDANT_STEM_LENGTH_INCHES`), and stays the object's to change
 * (`stemLengthInches`).
 *
 * Pure data; no SDK.
 */
const { PENDANT_STEM_LENGTH_INCHES } = require('./pendantHang');

const IN = 0.0254;

/** The flush-mount dome. */
const GENERIC_DOME = {
  style: 'dome',
  name: 'Dome',
  widthInches: 12,
  heightInches: 4.7,
};

/** Bands of a hung generic, top-down from the canopy face (`yMax` 0). */
function hungRegions({ canopy, stem, shade }) {
  const stemTop = -canopy.height;
  const shadeTop = stemTop - stem.height;
  return [
    {
      semantic: 'canopy',
      yMax: 0,
      yMin: stemTop,
      maxRadius: canopy.radius,
    },
    {
      semantic: 'stem',
      yMax: stemTop,
      yMin: shadeTop,
      maxRadius: stem.radius,
    },
    {
      semantic: 'shade',
      yMax: shadeTop,
      yMin: shadeTop - shade.height,
      maxRadius: shade.radius,
    },
  ];
}

const GENERIC_HUNG = {
  pendant: {
    style: 'pendant',
    name: 'Pendant',
    regions: hungRegions({
      canopy: { height: 2.1, radius: 2.37 },
      stem: { height: PENDANT_STEM_LENGTH_INCHES, radius: 0.83 },
      shade: { height: 13.2, radius: 6 },
    }),
  },
  chandelier: {
    style: 'chandelier',
    name: 'Chandelier',
    regions: hungRegions({
      canopy: { height: 3, radius: 4.8 },
      stem: { height: PENDANT_STEM_LENGTH_INCHES, radius: 0.86 },
      shade: { height: 11.6, radius: 17.5 },
    }),
  },
};

/** The generic styles a hung ceiling light can take. */
const HUNG_GENERIC_STYLES = Object.keys(GENERIC_HUNG);

/** Is this `proceduralStyle` one of the generics (not 'recessed', not none)? */
function isGenericCeilingLightStyle(style) {
  return style === GENERIC_DOME.style || HUNG_GENERIC_STYLES.includes(style);
}

/** A hung generic by its style ('pendant' / 'chandelier'), or null. */
function genericHungLight(style) {
  return GENERIC_HUNG[style] || null;
}

/** Height and widest radius of a band list, in inches. */
function extentOf(regions) {
  const top = Math.max(...regions.map((r) => r.yMax));
  const bottom = Math.min(...regions.map((r) => r.yMin));
  const radius = Math.max(...regions.map((r) => r.maxRadius || 0));
  return { heightInches: top - bottom, radiusInches: radius };
}

/**
 * Every generic ceiling light as the object fields and size a pick writes.
 * `fields` names every field another generic sets, null where this one does
 * not, so switching between them leaves nothing of the last behind.
 *
 * @returns {Array<{style: string, name: string, fields: Object,
 *   dimensions: {x: number, y: number, z: number}}>}
 */
function genericCeilingLightBuilds() {
  const dome = {
    style: GENERIC_DOME.style,
    name: GENERIC_DOME.name,
    fields: {
      proceduralStyle: 'dome',
      lightingSubtype: null,
      stemLengthInches: null,
    },
    dimensions: {
      x: GENERIC_DOME.widthInches * IN,
      y: GENERIC_DOME.heightInches * IN,
      z: GENERIC_DOME.widthInches * IN,
    },
  };
  const hung = HUNG_GENERIC_STYLES.map((style) => {
    const g = GENERIC_HUNG[style];
    const { heightInches, radiusInches } = extentOf(g.regions);
    const width = 2 * radiusInches * IN;
    return {
      style,
      name: g.name,
      fields: {
        proceduralStyle: style,
        lightingSubtype: style,
        stemLengthInches: PENDANT_STEM_LENGTH_INCHES,
      },
      // the box runs from the ceiling down to the bottom of the shade
      dimensions: { x: width, y: heightInches * IN, z: width },
    };
  });
  return [dome, ...hung];
}

/**
 * The generic pendant as a catalog MODEL describes itself — `materialRegions`
 * in inches and `dimensions` with their unit — so the planners that hang a
 * catalog pendant (`kitchenPendantPlan`, `pendantHang`) cut its stem for the
 * ceiling the same way. `proceduralStyle` rides along for the light object: it
 * is what has the scene draw the generic rather than look for a GLB.
 */
function genericPendantModel() {
  const g = GENERIC_HUNG.pendant;
  const { heightInches, radiusInches } = extentOf(g.regions);
  return {
    name: g.name,
    proceduralStyle: g.style,
    materialRegions: g.regions,
    dimensions: {
      unit: 'inches',
      width: 2 * radiusInches,
      height: heightInches,
      depth: 2 * radiusInches,
    },
  };
}

module.exports = {
  GENERIC_DOME,
  HUNG_GENERIC_STYLES,
  isGenericCeilingLightStyle,
  genericHungLight,
  genericCeilingLightBuilds,
  genericPendantModel,
};
